// กติกาของหน้าสแกน (กล้องและไฟล์ใช้ชุดเดียวกัน) — แยกจากคอมโพเนนต์ให้อ่าน/ทดสอบได้ในที่เดียว
//
//   บันทึกเอง   = รู้ตัวนักเรียนจากแถบรหัส + ชัดทุกข้อ + ยังไม่มีคำตอบเดิม (หรือเดิมต่าง 0 ข้อ = ไม่ต้องทำอะไร)
//   รอครูดู     = มีข้อไม่แน่ใจ / กระดาษสำรอง (เลขที่ฝนไม่มี CRC ต้องยืนยันทุกแผ่น) / มีคำตอบเดิมที่ต่าง /
//                 อ่านรหัสแผ่นไม่ได้หรือนักเรียนไม่อยู่ในห้อง (ให้เลือกคนเอง) / หลายแผ่นของคนเดียวกันในไฟล์เดียว
//   มีปัญหา     = อ่านไม่ได้เลย หรือเป็นกระดาษของข้อสอบชุดอื่น
import { fmt, matchSpare } from './examUtils'
import { cropIdDigits, cropRows } from './scanCrop'
import { idLayout } from './sheetLayout'

let nextId = 1

// คำตอบที่อ่านได้ -> { eq_no: code } ('1'..'5' / 'T' / 'F' / '#' / '') ครบทุกข้อปรนัย
export const readAnswers = (res, objective) =>
  Object.fromEntries(objective.map((q) => [q.eq_no, res.answers?.[q.eq_no] ?? '']))

// คำตอบที่บันทึกไว้แล้วของนักเรียน -> { eq_no: code } หรือ null ถ้ายังไม่มี
export const savedAnswers = (student, objective) => (student?.answers
  ? Object.fromEntries(objective.map((q) => [q.eq_no, student.answers[q.eq_no]?.ea_answer ?? '']))
  : null)

// ข้อที่ต่างจากที่บันทึกไว้ (ไม่มีของเดิม = ไม่ต่าง)
export const diffNos = (answers, saved, objective) => (saved
  ? objective.filter((q) => (answers[q.eq_no] ?? '') !== (saved[q.eq_no] ?? '')).map((q) => q.eq_no)
  : [])

// ผลการอ่านหนึ่งแผ่น -> รายการในหน้าสแกน (ยังไม่มีภาพตัดรายข้อ — ใส่ด้วย withCrops เฉพาะแผ่นที่ต้องให้ครูดู)
export const buildItem = ({ res, students, objective, thumb, label, source }) => {
  const base = { id: nextId++, label, source, thumb, markers: res.markers || null }
  if (!res.ok && !res.noCode) {
    return { ...base, state: 'problem', message: res.error || 'อ่านกระดาษไม่ได้' }
  }
  let studyId = null
  let identity = null   // ข้อความเมื่อยังไม่รู้ว่าเป็นของใคร
  let spareMatch = null
  if (res.noCode) {
    identity = 'อ่านรหัสแผ่นไม่ได้ — เลือกนักเรียนเอง'
  } else if (res.spare) {
    spareMatch = matchSpare(res, students)
    studyId = spareMatch.student?.study_id ?? null
    identity = spareMatch.problem
  } else if (students.some((s) => s.study_id === res.studyId)) {
    studyId = res.studyId
  } else {
    identity = 'นักเรียนของแผ่นนี้ไม่ได้อยู่ในห้องที่ใช้ข้อสอบนี้ — เลือกนักเรียนเอง'
  }
  const answers = readAnswers(res, objective)
  const student = students.find((s) => s.study_id === studyId)
  const changed = diffNos(answers, savedAnswers(student, objective), objective)
  const uncertain = res.uncertain || []
  return {
    ...base,
    studyId,
    identity,
    spare: !!res.spare,
    version: res.version ?? null,
    idRead: res.idRead || null,
    rows: res.rows || [],
    uncertain,
    answers,
    changed,                // ข้อที่ต่างจากที่บันทึกไว้ ณ ตอนอ่าน (ไว้เลือกว่าจะตัดภาพข้อไหน)
    crops: {},
    idCrop: null,
    idRows: res.idRows || [],
    state: 'review',
  }
}

// ระยะ (มม.) จากแถวเลข 0 ขึ้นไปถึงขอบบนของป้าย "เลขประจำตัว" / ช่องเขียนเลข ตามผังของแบบกระดาษนั้น
// titleY เป็นเส้นฐานของตัวอักษร จึงเผื่อความสูงตัวอักษรอีก 3 มม.
export const idHead = (version, choices) => {
  try {
    const l = idLayout(version ?? undefined, choices)
    const col = l.digits[0].bubbles
    const top = Math.min(...l.boxes.map((b) => b.y), l.titleY - 3)
    return { mmUp: col[0].cy - top, mmSpan: col[col.length - 1].cy - col[0].cy }
  } catch {
    return null   // แบบที่ไม่รู้จัก — ครอบแค่ช่องฝน
  }
}

// ตัดภาพรายข้อจากภาพความละเอียดเต็มของแผ่นนี้ (src = canvas) — ข้อไม่แน่ใจ + ข้อที่ต่างจากของเดิม
// กระดาษสำรองตัดแผงเลขประจำตัวพร้อมช่องที่นักเรียนเขียนเลขด้วยมือด้วย (choices = exam_choices กำหนดตำแหน่งแผง)
export const withCrops = (item, src, choices) => ({
  ...item,
  crops: cropRows(src, item.rows, new Set([...item.uncertain, ...(item.changed || [])])),
  idCrop: item.spare ? cropIdDigits(src, item.idRows, idHead(item.version, choices)) : null,
})

// แผ่นนี้บันทึกเองได้ไหม — 'save' / 'same' (ตรงกับที่บันทึกไว้แล้ว ไม่ต้องทำอะไร) / 'review'
export const decide = (item, students, objective) => {
  if (item.state !== 'review' || item.identity || item.spare || item.uncertain.length) return 'review'
  const student = students.find((s) => s.study_id === item.studyId)
  if (!student) return 'review'
  const saved = savedAnswers(student, objective)
  if (!saved) return 'save'
  return diffNos(item.answers, saved, objective).length ? 'review' : 'same'
}

// ทำไมแผ่นนี้ต้องให้ครูดู — ข้อความสั้นสำหรับแถวในรายการ
export const reviewReason = (item, student, objective) => {
  const parts = []
  if (item.spare) parts.push('กระดาษสำรอง — ยืนยันชื่อ')
  else if (item.identity || !student) parts.push('ยังไม่รู้ว่าเป็นของใคร')
  if (item.dupe) parts.push('มีหลายแผ่นของคนนี้')
  if (item.uncertain.length) parts.push(`ต้องดู ${item.uncertain.length} ข้อ`)
  const changed = diffNos(item.answers, savedAnswers(student, objective), objective).length
  if (changed) parts.push(`ต่างจากที่บันทึกไว้ ${changed} ข้อ`)
  return parts.join(' · ') || 'รอยืนยัน'
}

// ส่งคำตอบ (รหัส) ของหนึ่งแผ่นในรูปที่ endpoint บันทึกรับ
export const responseOf = (studyId, answers, objective) =>
  ({ study_id: studyId, answers: Object.fromEntries(objective.map((q) => [q.eq_no, answers[q.eq_no] ?? ''])) })

// คะแนนที่ได้ -> { main: '18/20', note: 'อัตนัยรอตรวจ 2 ข้อ' | null }
// score มาจาก server (ตัวคิดคะแนนเดียวกับหน้าผลสอบ) ไม่คิดเองในหน้านี้
export const scoreText = (score, total) => {
  if (!score) return null
  const notes = []
  if (score.pending?.length) notes.push(`อัตนัยรอตรวจ ${score.pending.length} ข้อ`)
  if (score.unkeyed?.length) notes.push(`ยังไม่มีเฉลย ${score.unkeyed.length} ข้อ`)
  return { main: `${fmt(score.total)}/${fmt(total)}`, note: notes.join(' · ') || null }
}
